// Stage / Group / Gameday detail views + GameCard.
//
// Data note: games in the stages payload carry only { id, title, statsMode,
// startDateTime, place } — no home/away teams or scores — so GameCard shows the
// scheduling info, not a box-score. (Tracked as a backend gap.)

import React, { useState } from 'react'
import TOKENS, { avatarColor } from './tokens'
import {
  Icon, Badge, Card, Avatar, Button, IconBtn, Empty, Tabs, ProgressBar, ExpandCollapseControl, useHoverCard,
} from './Primitives'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { DragHandle } from './Primitives'
import { fmtDateShort, fmtDateRange, fmtTime, periodTone, toneLabel, prettyGroupType, isFinal, isLive, countPlayed, gamedayBadge, applyDrag } from './helpers'
import buildBracketRounds from '../../../utils/helpers/buildBracketRounds'
import GROUP_TYPES from '../../../utils/constants/groupTypes'
import { isLiteStatsMode } from '../../../utils/constants/statsModeOptions'

// "time · Home vs Away" (with score when final), falling back to the game title.
const gameLine = (g) => {
  if (g.homeTeam && g.awayTeam) {
    const score = isFinal(g) ? ` ${g.homeTeam.score ?? 0}–${g.awayTeam.score ?? 0}` : ''
    return `${g.homeTeam.name} vs ${g.awayTeam.name}${score}`
  }
  return g.title || 'Game'
}

const PAGE = { padding: 24, maxWidth: 1240, margin: '0 auto' }

const Crumb = ({ items }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, fontWeight: 500, color: TOKENS.text3, marginBottom: 2 }}>
    {items.map((it, i) => (
      <React.Fragment key={i}>
        {i > 0 && <Icon name="chevronRight" size={12} />}
        {it.onClick ? (
          <button type="button" onClick={it.onClick} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', padding: 0, font: 'inherit' }}>{it.label}</button>
        ) : (
          <span>{it.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
)

/* ============================================================== Stage ====== */

export const StageView = ({ season, stage, nav, actions }) => {
  const hover = useHoverCard()
  const tone = periodTone(stage.startDate, stage.endDate)
  const groups = stage.groups || []
  const [collapsed, setCollapsed] = useState({})
  const toggleGroup = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }))
  const expandAll = () => setCollapsed({})
  const collapseAll = () => setCollapsed(Object.fromEntries(groups.map((g) => [g.id, true])))
  const allGroupsExpanded = groups.every((g) => !collapsed[g.id])

  return (
    <div style={PAGE}>
      <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 6, height: 40, borderRadius: 999, flexShrink: 0, background: tone === 'live' ? TOKENS.liveDot : tone === 'upcoming' ? TOKENS.upcomingDot : tone === 'past' ? TOKENS.pastDot : TOKENS.borderStrong }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Crumb items={[{ label: season.name, onClick: () => nav.openSeason() }, { label: 'Stage' }]} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: TOKENS.text, whiteSpace: 'nowrap', lineHeight: 1 }}>{stage.name}</h1>
            {tone !== 'neutral' && <Badge tone={tone} dot>{toneLabel(tone)}</Badge>}
            <span style={{ fontSize: 12, color: TOKENS.text3, fontFamily: TOKENS.mono }}>{fmtDateRange(stage.startDate, stage.endDate)}</span>
          </div>
        </div>
        {actions.editStage && <IconBtn icon="edit" tone="coral" title="Edit stage" onClick={() => actions.editStage(stage)} />}
        {actions.deleteStage && <IconBtn icon="trash" tone="danger" title="Delete stage" onClick={() => actions.deleteStage(stage)} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: TOKENS.text }}>Groups</h2>
          <Badge>{groups.length}</Badge>
          {groups.length > 1 && <ExpandCollapseControl allExpanded={allGroupsExpanded} onExpandAll={expandAll} onCollapseAll={collapseAll} style={{ marginLeft: 4 }} />}
        </div>
        {actions.createGroup && <Button variant="primary" icon="plus" onClick={() => actions.createGroup(stage)}>Add Group</Button>}
      </div>

      {groups.length === 0 ? (
        <Card><Empty icon="grid" title="No groups in this stage" hint={actions.createGroup ? 'Groups split a stage into pools or knockout brackets.' : undefined} action={actions.createGroup && <Button variant="primary" icon="plus" onClick={() => actions.createGroup(stage)}>Create the first group</Button>} /></Card>
      ) : (
        <DragDropContext onDragEnd={(r) => {
          if (!r.destination || r.source.index === r.destination.index) return
          applyDrag(groups, r.source.index, r.destination.index, (ids) => actions.reorderGroups(stage.id, ids))
        }}>
          <Droppable droppableId={`groups:${stage.id}`} type="GROUP">
            {(drop) => (
              <div ref={drop.innerRef} {...drop.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {groups.map((group, gi) => (
                  <Draggable key={group.id} draggableId={group.id} index={gi} isDragDisabled={!actions.reorderGroups}>
                    {(drag) => (
                      <div ref={drag.innerRef} {...drag.draggableProps} style={drag.draggableProps.style}>
                        <StageGroupCard stage={stage} group={group} nav={nav} actions={actions} hover={hover} expanded={!collapsed[group.id]} onToggle={() => toggleGroup(group.id)} dragHandleProps={actions.reorderGroups ? drag.dragHandleProps : undefined} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {drop.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  )
}

const StageGroupCard = ({ stage, group, nav, actions, hover, expanded, onToggle, dragHandleProps }) => {
  const gamedays = group.gamedays || []
  return (
    <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16 }}>
        {dragHandleProps && <DragHandle handleProps={dragHandleProps} />}
        <IconBtn icon={expanded ? 'chevronDown' : 'chevronRight'} onClick={onToggle} title={expanded ? 'Collapse' : 'Expand'} />
        <div style={{ width: 36, height: 36, borderRadius: TOKENS.radiusSm, background: TOKENS.coralSoft, color: TOKENS.coralDark, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="grid" size={16} />
        </div>
        <button type="button" onClick={() => nav.openGroup(stage, group)} className={hover.nameHover} style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'inherit' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'inherit' }}>{group.name}</h3>
            <Badge tone="coral">{prettyGroupType(group.type)}</Badge>
          </div>
          <div style={{ fontSize: 11, color: TOKENS.text3, marginTop: 2 }}>
            {Array.isArray(group.teams) ? `${group.teams.length} teams · ` : ''}{gamedays.length} gamedays
          </div>
        </button>
        {actions.editGroup && <IconBtn icon="edit" tone="coral" title="Edit group" onClick={() => actions.editGroup(group)} />}
        {actions.deleteGroup && <IconBtn icon="trash" tone="danger" title="Delete group" onClick={() => actions.deleteGroup(group)} />}
      </div>
      {expanded && (
        <div style={{ padding: '4px 16px 16px', background: TOKENS.surface, borderTop: `1px solid ${TOKENS.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', color: TOKENS.text3, textTransform: 'uppercase', margin: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Gamedays in this group</span>
            {actions.createGamedayBulk && (
              <button type="button" onClick={() => actions.createGamedayBulk(group)} style={{ color: TOKENS.coral, fontSize: 11, fontWeight: 500, border: 'none', background: 'transparent', cursor: 'pointer', textTransform: 'none', letterSpacing: 'normal', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon name="plus" size={11} /> Add gamedays
              </button>
            )}
          </div>
          {gamedays.length === 0 ? (
            <div style={{ fontSize: 11, color: TOKENS.text3, fontStyle: 'italic', padding: '4px 0' }}>No gamedays yet.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {gamedays.map((gd) => (
                <button key={gd.id} type="button" onClick={() => nav.openGameday(stage, group, gd)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radiusSm, fontSize: 12, cursor: 'pointer' }}>
                  <span style={{ fontWeight: 600, color: TOKENS.text }}>{gd.name}</span>
                  {gd.startDate ? (
                    <span style={{ fontSize: 11, fontFamily: TOKENS.mono, color: TOKENS.text3 }}>{fmtDateShort(gd.startDate)}</span>
                  ) : (
                    <Badge>Unscheduled</Badge>
                  )}
                  <span style={{ fontSize: 10, fontFamily: TOKENS.mono, color: TOKENS.text3 }}>· {(gd.games || []).length}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ============================================================== Group ====== */

export const GroupView = ({ season, stage, group, nav, actions }) => {
  const [tab, setTab] = useState('gamedays')
  const teams = Array.isArray(group.teams) ? group.teams : []
  // Knockout groups show a bracket instead of a standings table.
  const isBracket = group.type === GROUP_TYPES.BRACKET
  return (
    <div style={PAGE}>
      <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 6, height: 40, borderRadius: 999, background: TOKENS.coral, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Crumb items={[{ label: season.name, onClick: () => nav.openSeason() }, { label: stage.name, onClick: () => nav.openStage(stage) }, { label: 'Group' }]} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: TOKENS.text, whiteSpace: 'nowrap', lineHeight: 1 }}>{group.name}</h1>
            <Badge tone="coral">{prettyGroupType(group.type)}</Badge>
            <span style={{ fontSize: 12, color: TOKENS.text3, fontFamily: TOKENS.mono }}>{teams.length} teams · {(group.gamedays || []).length} gamedays</span>
          </div>
        </div>
        {actions.editGroup && <IconBtn icon="edit" tone="coral" title="Edit group" onClick={() => actions.editGroup(group)} />}
        {actions.deleteGroup && <IconBtn icon="trash" tone="danger" title="Delete group" onClick={() => actions.deleteGroup(group)} />}
      </div>

      <div style={{ marginTop: 4 }}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: 'gamedays', label: 'Gamedays', count: (group.gamedays || []).length },
            { id: 'teams', label: 'Group Teams', count: teams.length },
            { id: 'standings', label: isBracket ? 'Bracket' : 'Standings' },
          ]}
        />
      </div>

      <div style={{ paddingTop: 20 }}>
        {tab === 'gamedays' && <GamedaysPanel stage={stage} group={group} nav={nav} actions={actions} />}
        {tab === 'teams' && <GroupTeamsPanel group={group} actions={actions} assignButton={actions.assignTeamGroupButton && actions.assignTeamGroupButton(group)} />}
        {tab === 'standings' && (isBracket
          ? <BracketPanel group={group} actions={actions} />
          : <StandingsPanel group={group} actions={actions} />)}
      </div>
    </div>
  )
}

// Inner body of a gameday card (header + games preview + footer), shared between the
// grid view and the reorder list. `handleProps` (when reordering) renders a drag handle.
const GamedayCard = ({ stage, group, gd, i, nav, actions, handleProps }) => {
  const tone = periodTone(gd.startDate, gd.endDate)
  const games = gd.games || []
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        {handleProps && <DragHandle handleProps={handleProps} />}
        <button type="button" onClick={() => nav.openGameday(stage, group, gd)} style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: TOKENS.radiusSm, background: TOKENS.surface2, color: TOKENS.text2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>
                {gamedayBadge(gd, i)}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.text }}>{gd.name}</div>
                <div style={{ fontSize: 11, color: TOKENS.text3, fontFamily: TOKENS.mono }}>{fmtDateRange(gd.startDate, gd.endDate)}</div>
              </div>
            </div>
            {tone === 'neutral' ? <Badge>Unscheduled</Badge> : <Badge tone={tone} dot>{toneLabel(tone)}</Badge>}
          </div>
          {games.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {games.slice(0, 3).map((g) => (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <span style={{ color: TOKENS.text3, fontFamily: TOKENS.mono, width: 42, textAlign: 'right' }}>{fmtTime(g.startDateTime)}</span>
                  <span style={{ color: TOKENS.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{gameLine(g)}</span>
                </div>
              ))}
              {games.length > 3 && <div style={{ fontSize: 11, color: TOKENS.text3, paddingLeft: 50 }}>+ {games.length - 3} more</div>}
            </div>
          ) : (
            <div style={{ fontSize: 11.5, color: TOKENS.text3, fontStyle: 'italic', marginBottom: 12, paddingLeft: 4 }}>No games scheduled yet</div>
          )}
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingTop: 10, borderTop: `1px solid ${TOKENS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, color: TOKENS.text3, whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 600, color: TOKENS.text, fontVariantNumeric: 'tabular-nums' }}>{countPlayed(games)}</span>/<span style={{ fontVariantNumeric: 'tabular-nums' }}>{games.length}</span> games
          </div>
          <div style={{ flex: 1, maxWidth: 80 }}><ProgressBar value={countPlayed(games)} total={games.length || 1} /></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {actions.createGame && <Button variant="subtle" size="sm" icon="plus" onClick={() => actions.createGame(gd)}>Game</Button>}
          {actions.editGameday && <IconBtn icon="edit" tone="coral" title="Edit gameday" onClick={() => actions.editGameday(group, gd)} />}
          {actions.deleteGameday && <IconBtn icon="trash" tone="danger" title="Delete gameday" onClick={() => actions.deleteGameday(gd)} />}
        </div>
      </div>
    </>
  )
}

const GamedaysPanel = ({ stage, group, nav, actions }) => {
  const hover = useHoverCard()
  const gamedays = group.gamedays || []
  const [reordering, setReordering] = useState(false)
  const cardStyle = { background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: 16, cursor: 'default' }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 12, color: TOKENS.text3 }}>{gamedays.length} gameday{gamedays.length === 1 ? '' : 's'}</div>
          {actions.reorderGamedays && gamedays.length > 1 && (
            <Button variant="subtle" size="sm" icon={reordering ? 'grid' : 'list'} onClick={() => setReordering((v) => !v)}>
              {reordering ? 'Grid' : 'List'}
            </Button>
          )}
        </div>
        {actions.createGamedayBulk && <Button variant="primary" icon="plus" onClick={() => actions.createGamedayBulk(group)}>Add Gamedays</Button>}
      </div>

      {reordering && actions.reorderGamedays ? (
        <DragDropContext onDragEnd={(r) => {
          if (!r.destination || r.source.index === r.destination.index) return
          applyDrag(gamedays, r.source.index, r.destination.index, (ids) => actions.reorderGamedays(group.id, ids))
        }}>
          <Droppable droppableId={`gamedays:${group.id}`} type="GAMEDAY">
            {(drop) => (
              <div ref={drop.innerRef} {...drop.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {gamedays.map((gd, i) => (
                  <Draggable key={gd.id} draggableId={gd.id} index={i}>
                    {(drag) => (
                      <div ref={drag.innerRef} {...drag.draggableProps} className={hover.card} style={{ ...cardStyle, ...drag.draggableProps.style }}>
                        <GamedayCard stage={stage} group={group} gd={gd} i={i} nav={nav} actions={actions} handleProps={drag.dragHandleProps} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {drop.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {gamedays.map((gd, i) => (
            <div key={gd.id} className={hover.card} style={cardStyle}>
              <GamedayCard stage={stage} group={group} gd={gd} i={i} nav={nav} actions={actions} />
            </div>
          ))}
          {actions.createGamedayBulk && (
            <button type="button" onClick={() => actions.createGamedayBulk(group)} style={{ border: `2px dashed ${TOKENS.borderStrong}`, borderRadius: TOKENS.radius, color: TOKENS.text3, background: 'transparent', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 500, minHeight: 180, fontFamily: TOKENS.font }}>
              <Icon name="plus" size={18} /> Add gamedays
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const GroupTeamsPanel = ({ group, actions, assignButton }) => {
  const hover = useHoverCard()
  const teams = Array.isArray(group.teams) ? group.teams : []
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: TOKENS.text3 }}>{teams.length} team{teams.length === 1 ? '' : 's'} in this group</div>
        {assignButton}
      </div>
      {teams.length === 0 ? (
        <Card><Empty icon="users" title="No teams in this group" hint={assignButton ? 'Assign teams from the season pool to this group.' : undefined} action={assignButton} /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {teams.map((t, i) => (
            <div key={t.seasonTeamId} style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 10, fontFamily: TOKENS.mono, color: TOKENS.text3, width: 20, textAlign: 'center' }}>#{i + 1}</div>
              <button type="button" onClick={() => actions.openTeamStats(t)} className={hover.nameHover} title="View team statistics" style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, textAlign: 'left', color: 'inherit' }}>
                <Avatar name={t.teamName} src={t.teamLogoUrl} color={avatarColor(t.teamName || '')} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.teamName}</div>
                </div>
              </button>
              {actions.removeTeamFromGroup && <IconBtn icon="trash" tone="danger" title="Remove from group" onClick={() => actions.removeTeamFromGroup(group, t)} />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Standings entries carry only seasonTeamId + stats (no team name/logo), so we join
// them against the group's teams. Backend returns them ordered by position; we re-sort
// defensively. Falls back to the empty state until games have been played.
const StandingsPanel = ({ group, actions }) => {
  const hover = useHoverCard()
  const teams = Array.isArray(group.teams) ? group.teams : []
  const standings = (Array.isArray(group.standings) ? group.standings : [])
    .map((s) => {
      const team = teams.find((t) => t.seasonTeamId === s.seasonTeamId)
      return { ...s, teamId: team?.teamId, teamName: team?.teamName || 'Unknown', teamLogoUrl: team?.teamLogoUrl || null }
    })
    .sort((a, b) => (a.position || 0) - (b.position || 0))

  if (standings.length === 0) {
    return <Card><Empty icon="grid" title="Standings will appear here" hint="Once games are played, this view shows the live group table." /></Card>
  }

  const cols = [
    { key: 'gamesPlayed', label: 'P', title: 'Games played' },
    { key: 'wins', label: 'W', title: 'Wins' },
    { key: 'losses', label: 'L', title: 'Losses' },
    { key: 'pointsFor', label: 'PF', title: 'Points for' },
    { key: 'pointsAgainst', label: 'PA', title: 'Points against' },
    { key: 'pointDifferential', label: '+/-', title: 'Point differential' },
  ]
  const th = { padding: '10px 12px', fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: TOKENS.text3, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }
  const td = { padding: '10px 12px', fontSize: 13, color: TOKENS.text2, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }

  return (
    <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: TOKENS.surface }}>
            <th style={{ ...th, width: 40 }}>#</th>
            <th style={{ ...th, textAlign: 'left' }}>Team</th>
            {cols.map((c) => <th key={c.key} style={th} title={c.title}>{c.label}</th>)}
            <th style={{ ...th, color: TOKENS.text }} title="Points">Pts</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((s, i) => (
            <tr key={s.seasonTeamId || i} style={{ borderTop: `1px solid ${TOKENS.border}` }}>
              <td style={{ ...td, color: TOKENS.text3, fontFamily: TOKENS.mono }}>{s.position || i + 1}</td>
              <td style={{ ...td, textAlign: 'left' }}>
                <button type="button" onClick={() => actions.openTeamStats(s)} className={hover.nameHover} title="View team statistics" style={{ display: 'flex', alignItems: 'center', gap: 10, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, textAlign: 'left', color: 'inherit' }}>
                  <Avatar name={s.teamName} src={s.teamLogoUrl} color={avatarColor(s.teamName || '')} size={28} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: TOKENS.text }}>{s.teamName}</span>
                </button>
              </td>
              {cols.map((c) => <td key={c.key} style={td}>{s[c.key] ?? 0}</td>)}
              <td style={{ ...td, fontWeight: 700, color: TOKENS.text }}>{s.points ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ============================================================= Bracket ===== */

// Knockout groups have no meaningful standings — instead we show the bracket as columns
// of rounds (one per gameday, in order), each round a stack of best-of-N series cards.
// The DB encodes no bracket topology, so there are no connector lines; series are derived
// purely from games sharing a team-pair within a round. Clicking any score opens the same
// game menu used across the CMS (actions.openGame).
const BracketPanel = ({ group, actions }) => {
  const hover = useHoverCard()
  const games = (group.gamedays || []).flatMap((gd) => gd.games || [])
  const { mode, rounds } = buildBracketRounds(games, group.gamedays || [])

  // Bracket teams carry only teamId (from the game's EventTeam). The team-stats nav wants
  // the season-team (it reads seasonTeamId), so resolve back to the group's team list.
  const groupTeamByTeamId = new Map(
    (group.teams || []).filter((t) => t.teamId).map((t) => [t.teamId, t]),
  )
  const openTeam = (team) => {
    if (!team.teamId) return
    actions.openTeamStats?.(groupTeamByTeamId.get(team.teamId) || team)
  }

  if (rounds.every((r) => r.series.length === 0)) {
    return (
      <Card>
        <Empty
          icon="grid"
          title="Bracket will appear here"
          hint="Once knockout games are scheduled, this view shows the matchups."
        />
      </Card>
    )
  }

  // 'pairs': gamedays are legs of a series — one card per pair, stacked vertically so the
  // full team name always fits alongside the (possibly many) per-game score cells.
  if (mode === 'pairs') {
    const series = rounds[0]?.series || []
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 640 }}>
        {series.map((s) => <SeriesCard key={s.key} series={s} actions={actions} onTeamClick={openTeam} hover={hover} />)}
      </div>
    )
  }

  // 'rounds': gamedays are knockout rounds — one column per round.
  return (
    <div style={{ width: '100%', overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', minWidth: 'min-content' }}>
        {rounds.map((round) => (
          <div key={round.id} style={{ display: 'flex', flexDirection: 'column', minWidth: 264 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: TOKENS.text3, padding: '0 2px 12px', whiteSpace: 'nowrap' }}>
              {round.name || '—'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {round.series.map((series) => (
                <SeriesCard key={series.key} series={series} actions={actions} onTeamClick={openTeam} hover={hover} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const SeriesCard = ({ series, actions, onTeamClick, hover }) => (
  <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, overflow: 'hidden' }}>
    <SeriesTeamRow team={series.teamA} games={series.games} side="A" actions={actions} onTeamClick={onTeamClick} hover={hover} divider />
    <SeriesTeamRow team={series.teamB} games={series.games} side="B" actions={actions} onTeamClick={onTeamClick} hover={hover} />
  </div>
)

const SeriesTeamRow = ({ team, games, side, actions, onTeamClick, hover, divider }) => {
  const clickable = Boolean(team.teamId)
  const nameStyle = { flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderBottom: divider ? `1px solid ${TOKENS.border}` : 'none' }}>
      {clickable ? (
        <button
          type="button"
          title="View team statistics"
          onClick={() => onTeamClick?.(team)}
          className={hover?.nameHover}
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'inherit' }}
        >
          <Avatar name={team.name} src={team.logoUrl} color={avatarColor(team.name || '')} size={24} />
          <span title={team.name} style={nameStyle}>{team.name || '—'}</span>
        </button>
      ) : (
        <>
          <Avatar name={team.name} src={team.logoUrl} color={avatarColor(team.name || '')} size={24} />
          <span title={team.name} style={nameStyle}>{team.name || '—'}</span>
        </>
      )}
      <div style={{ display: 'flex', flexShrink: 0 }}>
        {games.map((g, idx) => {
          const value = side === 'A' ? g.scoreA : g.scoreB
          const other = side === 'A' ? g.scoreB : g.scoreA
          // Bold the higher score of each individual game (per-game winner), not the row.
          const won = value != null && other != null && value > other
          return (
            <button
              key={g.id || idx}
              type="button"
              title="View game"
              onClick={(e) => { e.stopPropagation(); if (g.id) actions.openGame?.(g.id, e) }}
              style={{ width: 38, minWidth: 38, textAlign: 'center', border: 'none', borderLeft: `1px solid ${TOKENS.border}`, background: 'transparent', font: 'inherit', fontSize: 13, fontWeight: won ? 700 : 500, color: won ? TOKENS.text : TOKENS.text3, cursor: 'pointer', padding: '2px 0', fontVariantNumeric: 'tabular-nums' }}
            >
              {value == null ? '–' : value}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ============================================================= Gameday ===== */

export const GamedayView = ({ season, stage, group, gameday, nav, actions }) => {
  const games = gameday.games || []
  const tone = periodTone(gameday.startDate, gameday.endDate)
  return (
    <div style={PAGE}>
      <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: TOKENS.radiusSm, background: TOKENS.coral, color: TOKENS.white, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 15, fontWeight: 700 }}>
          {gamedayBadge(gameday)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Crumb items={[{ label: season.name, onClick: () => nav.openSeason() }, { label: stage.name, onClick: () => nav.openStage(stage) }, { label: group.name, onClick: () => nav.openGroup(stage, group) }]} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: TOKENS.text, whiteSpace: 'nowrap', lineHeight: 1 }}>{gameday.name}</h1>
            {tone === 'neutral' ? <Badge>Unscheduled</Badge> : <Badge tone={tone} dot>{toneLabel(tone)}</Badge>}
            <span style={{ fontSize: 12, color: TOKENS.text3, fontFamily: TOKENS.mono }}>{fmtDateRange(gameday.startDate, gameday.endDate)}</span>
          </div>
        </div>
        {actions.editGameday && <IconBtn icon="edit" tone="coral" title="Edit gameday" onClick={() => actions.editGameday(group, gameday)} />}
        {actions.deleteGameday && <IconBtn icon="trash" tone="danger" title="Delete gameday" onClick={() => actions.deleteGameday(gameday)} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: TOKENS.text }}>Games</h2>
          <Badge>{games.length}</Badge>
        </div>
        {actions.createGame && <Button variant="primary" icon="plus" onClick={() => actions.createGame(gameday)}>New Game</Button>}
      </div>

      {games.length === 0 ? (
        <Card><Empty icon="play" title="No games scheduled" hint={actions.createGame ? 'Add the first game to this gameday — pick the teams, time and venue.' : undefined} action={actions.createGame && <Button variant="primary" icon="plus" onClick={() => actions.createGame(gameday)}>Schedule a game</Button>} /></Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {games.map((g) => <GameCard key={g.id} game={g} actions={actions} />)}
        </div>
      )}
    </div>
  )
}

const GameCard = ({ game, actions }) => {
  const hover = useHoverCard()
  const final = isFinal(game)
  const live = isLive(game)
  const home = game.homeTeam
  const away = game.awayTeam
  const homeWon = final && home && away && (home.score ?? 0) > (away.score ?? 0)
  const awayWon = final && home && away && (away.score ?? 0) > (home.score ?? 0)

  return (
    <div className={hover.card} onClick={(e) => actions.openGame?.(game.id, e)} style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: 16, display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }}>
      <div style={{ textAlign: 'center', width: 84, flexShrink: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: TOKENS.text3 }}>
          {game.startDateTime ? fmtDateShort(game.startDateTime).split(' ').slice(0, 2).join(' ') : '—'}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: TOKENS.text, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{game.startDateTime ? fmtTime(game.startDateTime) : '—'}</div>
      </div>
      <div style={{ width: 1, height: 40, background: TOKENS.border }} />

      {home && away ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, opacity: awayWon ? 0.5 : 1 }}>
            <Avatar name={home.name} src={home.logoUrl} color={home.color || avatarColor(home.name || '')} size={36} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{home.name}</div>
              <div style={{ fontSize: 11, color: TOKENS.text3 }}>Home</div>
            </div>
          </div>
          <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 80 }}>
            {final ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <span style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: homeWon ? TOKENS.text : TOKENS.text3 }}>{home.score ?? 0}</span>
                <span style={{ fontSize: 10, fontFamily: TOKENS.mono, color: TOKENS.text3 }}>FINAL</span>
                <span style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: awayWon ? TOKENS.text : TOKENS.text3 }}>{away.score ?? 0}</span>
              </div>
            ) : live ? (
              <Badge tone="live" dot>Live</Badge>
            ) : (
              <Badge tone="upcoming">vs</Badge>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, justifyContent: 'flex-end', opacity: homeWon ? 0.5 : 1 }}>
            <div style={{ textAlign: 'right', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{away.name}</div>
              <div style={{ fontSize: 11, color: TOKENS.text3 }}>Away</div>
            </div>
            <Avatar name={away.name} src={away.logoUrl} color={away.color || avatarColor(away.name || '')} size={36} />
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.title || 'Game'}</div>
            {live && <Badge tone="live" dot>Live</Badge>}
            {isLiteStatsMode(game.statsMode) && <Badge tone="coral">Lite</Badge>}
          </div>
        </div>
      )}

      <div style={{ width: 1, height: 40, background: TOKENS.border }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 150, flexShrink: 0 }}>
        {game.place && (
          <>
            <Icon name="mapPin" size={13} style={{ color: TOKENS.text3, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: TOKENS.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.place}</span>
          </>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {actions.editGame && <IconBtn icon="edit" tone="coral" title="Edit game" onClick={(e) => { e.stopPropagation(); actions.editGame(game.id) }} />}
        {actions.deleteGame && <IconBtn icon="trash" tone="danger" title="Delete game" onClick={(e) => { e.stopPropagation(); actions.deleteGame(game) }} />}
      </div>
    </div>
  )
}
